import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { useGetBuildingsByIdQuery, usePostAgreementImportsMutation } from '@client/api';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton, Typography } from '@client/toolkit';

import { PortfolioPaths } from '../../../../../portfolio.router';
import { extractErrorCodes } from '../utils/extract-error-codes';

interface StepSessionProps {
  onSessionCreated: (sessionId: string) => void;
}

/**
 * Step 1 — starts the import session for the current building. The Phase 0 (building
 * readiness) and Phase 1 (catalog coverage) gates both run server-side as part of this
 * single call; there's no separate GET endpoint to preview them, so failures surface here
 * as a list of translated validation error codes with a link to building setup.
 */
export function StepSession({ onSessionCreated }: StepSessionProps) {
  const { t: tAgreements } = useTranslation('agreements');
  const { buildingId } = useParams<{ buildingId: string }>();
  const navigate = useNavigate();

  const { data: building, isLoading: isLoadingBuilding } = useGetBuildingsByIdQuery(
    { id: buildingId ?? '' },
    { skip: !buildingId },
  );

  const [createSession, { isLoading: isCreating, error }] = usePostAgreementImportsMutation();

  const errorCodes = extractErrorCodes(error);

  const handleStart = async () => {
    if (!buildingId) return;
    try {
      const sessionId = await createSession({ body: { buildingSpaceId: buildingId } }).unwrap();
      onSessionCreated(sessionId);
    } catch {
      /* error is surfaced below via the errorCodes list */
    }
  };

  const handleGoToBuildingSetup = () => {
    navigate(generatePath(PortfolioPaths.BuildingEdit, { buildingId: buildingId ?? '' }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{tAgreements('agreementImport.session.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <Typography variant="secondary">
          {isLoadingBuilding ? (
            <Skeleton className="h-5 w-64" />
          ) : (
            <Trans
              t={tAgreements}
              i18nKey="agreementImport.session.description"
              values={{ buildingName: building?.name }}
              components={{ bold: <strong /> }}
            />
          )}
        </Typography>

        {errorCodes.length > 0 && (
          <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4 space-y-2">
            <div className="font-semibold text-red-300">{tAgreements('agreementImport.session.notReadyTitle')}</div>
            <ul className="list-disc pl-5 text-sm text-red-200 space-y-1">
              {errorCodes.map((code) => (
                <li key={code}>{tAgreements(`agreementImport.validationErrors.${code}`, { defaultValue: code })}</li>
              ))}
            </ul>
            <Button color="danger-light" size="sm" onClick={handleGoToBuildingSetup}>
              {tAgreements('agreementImport.session.goToBuildingSetup')}
            </Button>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            color="primary"
            disabled={!buildingId || isLoadingBuilding}
            progressing={isCreating}
            onClick={handleStart}
          >
            {tAgreements('agreementImport.session.startButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
